/**
 * Instalação do app (PWA) — regras do #291
 *
 * - O evento `beforeinstallprompt` é capturado desde o início (main.tsx) e SEMPRE
 *   tem o comportamento padrão cancelado: o navegador não mostra a própria barra
 *   "Instalar app" e nada aparece automaticamente. O evento fica guardado para quando
 *   o sistema decidir oferecer a instalação (opção manual no painel; convite após o
 *   pedido na loja online).
 * - O evento vale para o manifesto da página no momento em que chegou. O link do
 *   manifesto entra e sai conforme a área (@utils/appManifest); por isso o evento guarda
 *   o endereço do manifesto e só é usado se a página ainda estiver com o mesmo.
 * - iPhone/iPad não têm esse evento: a tela mostra as instruções do Safari
 *   (Compartilhar › Adicionar à Tela de Início) ou, dentro de navegador embutido de
 *   outro app (Instagram, Facebook, TikTok...), pede para abrir a página no Safari.
 *
 * Áreas do sistema:
 * - site institucional, blog, cadastro, login e mesa: sem manifesto, nunca há aviso de
 *   instalação (nem o do próprio navegador);
 * - loja online (/loja/:slug): o app instalado é o da loja; o manifesto dela só entra
 *   quando o convite pós-pedido vai aparecer;
 * - painel (rotas protegidas): manifesto do Vende Aí e só a opção manual
 *   "Instalar o app Vende Aí".
 *
 * @author Equipe VendeAi
 * @since 2.4.0
 */

import { useSyncExternalStore } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
  interface Navigator {
    /** Safari (iOS): aberto pelo ícone da tela de início */
    standalone?: boolean;
  }
}

interface InstallState {
  /** Evento guardado e o manifesto ao qual ele se refere */
  deferred: BeforeInstallPromptEvent | null;
  manifestHref: string | null;
  /** O app acabou de ser instalado nesta página */
  installed: boolean;
}

let state: InstallState = { deferred: null, manifestHref: null, installed: false };
const listeners = new Set<() => void>();
let started = false;

function setState(patch: Partial<InstallState>) {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
}

/** Endereço absoluto do manifesto atual da página (ou null) */
export function currentManifestHref(): string | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLLinkElement>('link[rel="manifest"]')?.href || null;
}

/** Começa a escutar os eventos de instalação (chamar uma vez, antes do React) */
export function startInstallPromptCapture(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  // Evento que chegou antes do app carregar (guardado pelo script do index.html)
  const early = (
    window as unknown as {
      __vendeaiInstallPrompt?: { event: BeforeInstallPromptEvent; manifestHref: string | null };
    }
  ).__vendeaiInstallPrompt;
  if (early?.event) setState({ deferred: early.event, manifestHref: early.manifestHref });
  window.addEventListener('beforeinstallprompt', event => {
    // Nunca deixa o navegador mostrar a barra própria de instalação
    event.preventDefault();
    setState({ deferred: event, manifestHref: currentManifestHref() });
  });
  window.addEventListener('appinstalled', () => {
    setState({ deferred: null, manifestHref: null, installed: true });
  });
}

/** Descarta o evento guardado (ex.: a página trocou de manifesto) */
export function resetInstallPrompt(): void {
  if (state.deferred || state.installed)
    setState({ deferred: null, manifestHref: null, installed: false });
}

/** Página aberta como app instalado (tela cheia, sem a barra do navegador) */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true
    );
  } catch {
    return false;
  }
}

/** iPhone, iPad ou iPod (inclui iPad que se apresenta como Mac) */
export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Navegador embutido de outro app no iPhone/iPad (Instagram, Facebook, Messenger,
 * TikTok, LinkedIn, Snapchat, Pinterest, Line, X/Twitter, app do Google...). Nele não
 * existe "Adicionar à Tela de Início": é preciso abrir a página no Safari.
 */
export function isIOSInAppBrowser(): boolean {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|musical_ly|TikTok|BytedanceWebview|LinkedInApp|Snapchat|Pinterest|\bLine\/|Twitter|GSA\//i.test(
    ua,
  );
}

/**
 * Abre o pedido de instalação do navegador.
 * @returns 'accepted' | 'dismissed' | 'unavailable' (sem evento guardado para esta página)
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const { deferred, manifestHref } = state;
  if (!deferred || manifestHref !== currentManifestHref()) return 'unavailable';
  // O evento só pode ser usado uma vez
  setState({ deferred: null, manifestHref: null });
  try {
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') setState({ installed: true });
    return outcome;
  } catch {
    return 'unavailable';
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

export interface InstallAvailability {
  /** O navegador deixou instalar o app desta página (Chrome, Edge, Android) */
  canPrompt: boolean;
  /** iPhone/iPad fora do app: mostrar as instruções do Safari */
  showIOSInstructions: boolean;
  /** iPhone/iPad dentro do navegador de outro app: pedir para abrir no Safari */
  iosInAppBrowser: boolean;
  /** Já está aberto como app (ou acabou de ser instalado) */
  installed: boolean;
  /** Há algum jeito de oferecer a instalação */
  available: boolean;
}

/** Situação da instalação do app da página atual */
export function useInstallPrompt(): InstallAvailability {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const installed = snapshot.installed || isStandalone();
  const canPrompt =
    !installed && !!snapshot.deferred && snapshot.manifestHref === currentManifestHref();
  const showIOSInstructions = !installed && !canPrompt && isIOSDevice();
  return {
    canPrompt,
    showIOSInstructions,
    iosInAppBrowser: showIOSInstructions && isIOSInAppBrowser(),
    installed,
    available: canPrompt || showIOSInstructions,
  };
}

// ---------------------------------------------------------------------------
// Áreas do sistema
// ---------------------------------------------------------------------------

/** Rotas públicas (site, blog, autenticação, loja e mesa): fora do painel */
const PUBLIC_PREFIXES = [
  '/para/',
  '/blog',
  '/privacidade',
  '/entrar',
  '/cadastro',
  '/esqueci-senha',
  '/redefinir-senha',
  '/loja/',
  '/mesa/',
];

/** A rota é do painel administrativo (MainLayout, assinatura e fila de pedidos) */
export function isPanelPath(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return false;
  return !PUBLIC_PREFIXES.some(
    prefix => pathname === prefix.replace(/\/$/, '') || pathname.startsWith(prefix),
  );
}

// ---------------------------------------------------------------------------
// Convite da loja online (uma vez por loja, neste aparelho)
// ---------------------------------------------------------------------------

const inviteKey = (slug: string) => `vendeai:app-loja:${slug}`;
/** Cópia em memória (navegador sem acesso ao armazenamento local) */
const shownInMemory = new Set<string>();

/** O convite para instalar o app desta loja já foi mostrado (aceito ou recusado) */
export function wasStoreInviteShown(slug: string): boolean {
  if (shownInMemory.has(slug)) return true;
  try {
    return window.localStorage.getItem(inviteKey(slug)) !== null;
  } catch {
    return false;
  }
}

/** Grava que o convite desta loja já foi mostrado; nunca mais aparece sozinho */
export function markStoreInviteShown(
  slug: string,
  outcome: 'mostrado' | 'recusado' | 'instalado',
): void {
  shownInMemory.add(slug);
  try {
    window.localStorage.setItem(
      inviteKey(slug),
      JSON.stringify({ outcome, at: new Date().toISOString() }),
    );
  } catch {
    // Sem armazenamento: vale só enquanto a página estiver aberta
  }
}
